import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { ResetIcon } from '@dynatrace/strato-icons';

import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { isSameDomain } from '../../../core/utils/zoom.js';
import { useCurrentDomainState } from '../../hooks/store/selectors.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { useXScales } from '../../hooks/useXScale.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';
import { useResetInteractions } from '../overlays/hooks/useResetInteractions.js';

export const ResetButton = () => {
  const { initialScale } = useXScales();
  const intl = useIntl();
  const resetButtonRef = useRef<HTMLButtonElement>(null);
  const tabIndex = useA11yToolbarTabIndex('reset');
  const currentDomain = useCurrentDomainState();
  const { zoom, zoomX } = useChartInteractions();
  const resetInteractions = useResetInteractions();
  const { resetSelectionState } = useSynchronizedSelection();

  const hasZoomInteractions = zoom?.enabled || zoomX?.enabled;

  const handleClick = () => {
    resetInteractions();
    resetSelectionState?.();
  };

  /** Handles the zoom reset when a 'enter' keyboard event happens.
   * @param event - A keyboard event param.
   */
  function handleZoomResetKeyboardMode(event: ReactKeyboardEvent) {
    if (event.code === 'Enter') {
      resetInteractions();
    }
  }

  const isResettable = !isSameDomain(currentDomain, initialScale.domain());

  const onlyXZoomIsEnabled = zoomX?.enabled && !zoom?.enabled;

  return hasZoomInteractions ? (
    <ChartToolbarRenderer.Control
      ref={resetButtonRef}
      prefixIcon={<ResetIcon />}
      tooltipText={intl.formatMessage(TIMESERIES_MESSAGES.zoomActionReset)}
      tooltipKeyShortcut="R"
      testId="chart-toolbar-zoom-reset"
      onKeyDown={handleZoomResetKeyboardMode}
      onClick={handleClick}
      tabIndex={tabIndex}
      disabled={!isResettable}
      placement="toolbar"
      divider={onlyXZoomIsEnabled ? 'left' : undefined}
      showWhenCollapsed={isResettable}
    />
  ) : null;
};

ResetButton['displayName'] = 'ResetButton';
